import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useProductsContext } from "../../Contexts/SidebarContext";
import { NewCampaignDialog } from "./NewCampaignDialog";
import MarketingMessages from "./MarketingMessages";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import ToastManager from "../../Components/ToastManager";
import { useNavigate } from "react-router-dom";
import SearchBarWithButton from "../BroadcastFBManage/DMComponents/SearchBarWithButton";
import CampaignsList from "../BroadcastFBManage/DMComponents/CampaignsList";

interface Campaign {
  messageCampaignId: string;
  messageCampaignName: string;
  lifetimeBudget: number;
  campaignStatus: string;
}

interface ApiResponse {
  statusCode: string;
  messageCampaignDetails?: Campaign[];
  Message?: string;
}

const CampaignsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { sideBar } = useProductsContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [routeToMM, setRouteToMM] = useState(false);
  const [routeToCampaigns, setRouteToCampaigns] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId, adAccountName, adAccountCurrency } =
    readJson("fbbAdAccountDetail") || "{}";

  const filteredCampaigns = useMemo(
    () =>
      campaigns.filter((campaign) =>
        campaign.messageCampaignName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [campaigns, searchTerm],
  );

  const onSuccessCall = useCallback((data: string) => {
    const respData = CommonUtil.parseData(data) as ApiResponse;
    if (respData) {
      if (respData.statusCode === "BM0000" && respData.messageCampaignDetails) {
        setCampaigns(respData.messageCampaignDetails);
      } else {
        ToastManager.showToast("No message campaigns are available.", "Error");
      }
    }
  }, []);

  const onErrorCall = useCallback(() => {
    ToastManager.showToast("Failed to fetch campaigns.", "Error");
  }, []);

  const fetchCampaigns = useCallback(() => {
    if (!pageId || !adAccountId) {
      return;
    }

    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastMessageTemplates/getMessageCampaigns",
        "",
        pageDataReq,
        "GET",
        true,
        onSuccessCall,
        onErrorCall,
      );
    } catch (error) {
      ToastManager.showToast("Error fetching campaigns.", "Error");
    }
  }, [pageId, adAccountId, onSuccessCall, onErrorCall]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleOpenCreate = useCallback(() => {
    setOpenDialog(true);
    setEditCampaign(null);
  }, []);

  const handleRouteToMM = useCallback((campaign: Campaign) => {
    localStorage.setItem("selectedCampaign", JSON.stringify(campaign));
    setRouteToCampaigns(false);
    setRouteToMM(true);
  }, []);

  const handleRouteToCData = useCallback(() => {
    setRouteToCampaigns(false);
  }, []);

  const handleBackToCampaigns = useCallback(() => {
    setRouteToMM(false);
    setRouteToCampaigns(true);
  }, []);

  const handleBackToPages = useCallback(() => {
    navigate("/existingbroadcastpages");
  }, [navigate]);

  const handleSaveCampaign = useCallback((newCampaign: Campaign) => {
    setCampaigns((prev) => {
      const existingIndex = prev.findIndex(
        (campaign) =>
          campaign.messageCampaignId === newCampaign.messageCampaignId,
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = newCampaign;
        return updated;
      }

      return [...prev, newCampaign];
    });
  }, []);

  return (
    <Box
      className="content-page"
      sx={{
        ml: sideBar ? "250px" : "120px",
        backgroundColor: "#fff",
        mr: 10,
      }}>
      <Box className="content">
        {routeToCampaigns && !routeToMM && (
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* ── Row 1: Back button + Title ── */}
            <Grid container spacing={2} sx={{ alignItems: "center", mt: 2 }}>
              <Grid size={{ xs: 4 }}>
                <Button
                  variant="outlined"
                  sx={{ textTransform: "none", ml: 20 }}
                  onClick={handleBackToPages}>
                  ← Back To Pages
                </Button>
              </Grid>
              <Grid size={{ xs: 8 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: "bold", textAlign: "start", ml: 14 }}>
                  DM Campaigns
                </Typography>
              </Grid>
            </Grid>

            {/* ── Row 2: Search bar + Create button (full width, centred) ── */}
            <Box
              sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: "83.33%", lg: "66.66%" },
                }}>
                <SearchBarWithButton
                  placeholder="Search Campaign Name..."
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onNew={handleOpenCreate}
                  newBtnLabel="+ Create Campaign"
                />
              </Box>
            </Box>

            {/* ── Row 3: Campaigns list (scrollable, centred) ── */}
            <Box
              className="custom-scrollbar-css"
              sx={{
                height: "55vh",
                overflowY: "auto",
                display: "flex",
                justifyContent: "center",
              }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: "83.33%", lg: "66.66%" },
                }}>
                <CampaignsList
                  campaigns={filteredCampaigns}
                  adAccountName={adAccountName}
                  adAccountCurrency={adAccountCurrency}
                  onOpenMessages={handleRouteToMM}
                  onOpenCampaignData={handleRouteToCData}
                />
              </Box>
            </Box>
          </Box>
        )}
        <NewCampaignDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleSaveCampaign}
          editCampaign={editCampaign}
          fetchCampaigns={fetchCampaigns}
        />
      </Box>
      {routeToMM && (
        <MarketingMessages handleBackToCampaigns={handleBackToCampaigns} />
      )}
    </Box>
  );
};
export default CampaignsDashboard;
